/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { Fragment, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import useWebSocket, { ReadyState } from "react-use-websocket";
import config from "../../../config";
import formatError from "../../../services/FormatError";
import { chatAction } from "../../../store/actions/ChatActions";
import SideBar from "./SideBar";
import NavLogoSection from "./NavLogoSection";
import Header from "./Header";
import Notifications from "./Notifications/Notifications";
import ChatScreen from "./Chat/ChatScreen";
import { getNotificationsPerProfile } from "../../../services/NotificationsService";
import { getUnseenChatCount } from "../../../services/ChatService";

function Nav() {
  const dispatch = useDispatch();

  const [toggle, setToggle] = useState("");

  const role = JSON.parse(sessionStorage.getItem("userDetails"))?.result;

  const state = useSelector((state) => state.auth.auth.result);

  const [socketUrl, setSocketUrl] = useState(null);

  const wsOptions = {
    shouldReconnect: (closeEvent) => {
      console.log(
        "WebSocket reconnection attempt:",
        closeEvent.code,
        closeEvent.reason
      );
      return true;
    },
    onError: (event) => {
      console.error("WebSocket error:", event);
      console.error("Error details:", {
        type: event.type,
        target: event.target,
        readyState: event.target?.readyState,
        url: event.target?.url,
      });
    },
    onClose: (event) => {
      console.log("WebSocket closed:", {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
    },
    onOpen: () => {
      console.log("WebSocket connection opened successfully");
    },
    reconnectAttempts: 5,
    reconnectInterval: 3000,
  };

  let wsUrl = null;
  if (role?.role?.id !== 2) {
    wsUrl = socketUrl;
  }

  const {
    sendMessage: originalSendMessage,
    lastMessage,
    readyState,
  } = useWebSocket(wsUrl, wsOptions);

  const connectionStatus = {
    [ReadyState.CONNECTING]: "Connecting",
    [ReadyState.OPEN]: "Open",
    [ReadyState.CLOSING]: "Closing",
    [ReadyState.CLOSED]: "Closed",
    [ReadyState.UNINSTANTIATED]: "Uninstantiated",
  };

  // Safe sendMessage wrapper that checks connection state
  const sendMessage = useCallback(
    (message) => {
      if (readyState === ReadyState.OPEN) {
        try {
          originalSendMessage(message);
          console.log("Message sent successfully:", message);
        } catch (error) {
          console.error("Failed to send message:", error);
        }
      } else {
        console.warn(
          "Cannot send message - WebSocket not connected. State:",
          connectionStatus[readyState]
        );
      }
    },
    [originalSendMessage, readyState, connectionStatus]
  );

  const [seen, setSeen] = useState(false);

  const [tableParams, setTableParams] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [notificationsData, setNotificationsData] = useState([]);
  const [unseenMessageCount, setUnseenMessageCount] = useState(0);
  const lastFetchRef = React.useRef(0);
  const debounceTimerRef = React.useRef(null);

  useEffect(() => {
    if (role?.role?.id !== 2) {
      const wsUrl = config.wssUrl + config.routes.ws(role?.token);
      console.log("Setting WebSocket URL:", wsUrl);
      console.log("User token:", role?.token ? "Present" : "Missing");
      setSocketUrl(wsUrl);
    }

    // Superadmin and admin access
    // role?.role?.id === 1 || role?.role?.id === 2 || (role.hasOwnProperty('loggedinprofiletype') && role.loggedinprofiletype !== "")

    if (
      role.hasOwnProperty("loggedinprofiletype") &&
      role.loggedinprofiletype !== ""
    ) {
      // setSocketUrl(config.wssUrl + config.routes.ws(role?.token));

      getNotifications();
    }
  }, [state]);

  useEffect(() => {
    if (lastMessage !== null) {
      getNotifications();
      requestUnseenCount();
    }
  }, [lastMessage]);

  useEffect(() => {
    // Superadmin and admin access
    // role?.role?.id === 1 || role?.role?.id === 2 || (role.hasOwnProperty('loggedinprofiletype') && role.loggedinprofiletype !== "")
    if (
      role.hasOwnProperty("loggedinprofiletype") &&
      role.loggedinprofiletype !== ""
    ) {
      getNotifications();
    }
  }, [seen, tableParams.current, tableParams.pageSize]);

  useEffect(() => {
    // Heartbeat mechanism to keep the connection alive
    const interval = setInterval(() => {
      if (readyState === ReadyState.OPEN) {
        try {
          sendMessage(JSON.stringify({ type: "ping" }));
        } catch (error) {
          console.error("Failed to send ping message:", error);
        }
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [readyState, sendMessage]);

  useEffect(() => {
    requestUnseenCount(true);
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        requestUnseenCount(true);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const requestUnseenCount = (immediate = false) => {
    if (role?.role?.id === 1 || role?.role?.id === 2) return;
    if (!role?.loggedinprofiletype) return;
    if (document.visibilityState !== "visible") return;

    const now = Date.now();
    const MIN_INTERVAL_MS = 15000;
    const shouldRunImmediately =
      immediate || now - (lastFetchRef.current || 0) > MIN_INTERVAL_MS;

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const invoke = () => {
      lastFetchRef.current = Date.now();
      getUnseenChatCount()
        .then((res) => {
          const response = res.data?.result ?? 0;
          setUnseenMessageCount(response);
        })
        .catch(() => {});
    };

    if (shouldRunImmediately) {
      invoke();
    } else {
      debounceTimerRef.current = setTimeout(invoke, 1000);
    }
  };

  const getNotifications = () => {
    getNotificationsPerProfile(tableParams.current, tableParams.pageSize, seen)
      .then((res) => {
        const response = res.data?.result;

        setNotificationsData(response?.data ?? []);

        setTableParams((prevState) => ({
          ...prevState,
          total: response?.count ?? 0,
        }));
      })
      .catch((error) => {
        formatError(error.response?.data?.error);
      });
  };

  const onClick = (name) => setToggle(toggle === name ? "" : name);

  // Debug WebSocket connection status
  useEffect(() => {
    console.log("WebSocket ReadyState:", connectionStatus[readyState]);
    console.log("WebSocket URL:", wsUrl);

    // Manual WebSocket test for debugging
    if (wsUrl && readyState === ReadyState.CLOSED) {
      console.log("Testing WebSocket connection manually...");
      const testWs = new WebSocket(wsUrl);
      testWs.onopen = () => {
        console.log("Manual WebSocket test: Connection opened");
        testWs.close();
      };
      testWs.onerror = (error) => {
        console.error("Manual WebSocket test: Error", error);
      };
      testWs.onclose = (event) => {
        console.log("Manual WebSocket test: Closed", event.code, event.reason);
      };
    }
  }, [readyState, wsUrl]);

  return (
    <Fragment>
      <NavLogoSection />

      <Header
        onOpenNotification={() => onClick("notification")}
        notificationCount={tableParams?.total ?? 0}
        onOpenChat={() => dispatch(chatAction(true))}
        unseenMessageCount={unseenMessageCount}
        // onNotification={() => onClick("notification")}
        // onProfile={() => onClick("profile")}
        // toggle={toggle}
        // title={title}
        // onBox={() => onClick("box")}
        // onClick={() => ClickToAddEvent()}
      />

      <Notifications
        onClick={() => onClick("notification")}
        toggle={toggle}
        seen={seen}
        setSeen={setSeen}
        tableParams={tableParams}
        setTableParams={setTableParams}
        notificationsData={notificationsData}
        getNotifications={getNotifications}
      />

      <ChatScreen
        sendMessage={sendMessage}
        lastMessage={lastMessage}
        readyState={readyState}
      />

      <SideBar />
    </Fragment>
  );
}

export default Nav;
